import { useState } from "react";
import { Sparkles, Database, DollarSign, MessageSquare, FileText, Settings as SettingsIcon, ArrowLeft, BarChart3, LogOut, Users, Clock, Tag, Bot } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import ErrorBoundary from "@/components/ErrorBoundary";
import KnowledgeTab from "./admin/KnowledgeTab";
import PricingTab from "./admin/PricingTab";
import LeadsTab from "./admin/LeadsTab";
import InstructionsTab from "./admin/InstructionsTab";
import SettingsTab from "./admin/SettingsTab";
import AnalyticsTab from "./admin/AnalyticsTab";
import RecipientsTab from "./admin/RecipientsTab";
import WorkingHoursTab from "./admin/WorkingHoursTab";
import CampaignsTab from "./admin/CampaignsTab";
import AIAgentTab from "./admin/AIAgentTab";

const TABS = [
  { value: "agent", label: "AI Asistan", icon: Bot, comp: AIAgentTab, adminOnly: true },
  { value: "analytics", label: "Analiz", icon: BarChart3, comp: AnalyticsTab },
  { value: "knowledge", label: "Bilgi Tabanı", icon: Database, comp: KnowledgeTab },
  { value: "pricing", label: "Fiyatlar", icon: DollarSign, comp: PricingTab },
  { value: "campaigns", label: "Kampanyalar", icon: Tag, comp: CampaignsTab },
  { value: "hours", label: "Çalışma Saatleri", icon: Clock, comp: WorkingHoursTab },
  { value: "leads", label: "Konuşmalar / Lead'ler", icon: MessageSquare, comp: LeadsTab },
  { value: "recipients", label: "Yöneticiler", icon: Users, comp: RecipientsTab, adminOnly: true },
  { value: "instructions", label: "AI Komutları", icon: FileText, comp: InstructionsTab },
  { value: "settings", label: "Ayarlar", icon: SettingsIcon, comp: SettingsTab },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("agent");
  const { user, logout } = useAuth();
  const visibleTabs = TABS.filter((t) => !t.adminOnly || user?.role === "admin");

  const onLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border/50 bg-white/60 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-muted-foreground hover:text-foreground" data-testid="back-home">
              <ArrowLeft className="w-4 h-4" />
            </a>
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #B39B7A, #8B9B88)" }}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="font-serif text-lg leading-none">WebChat Admin</div>
              <div className="font-display text-xs text-muted-foreground tracking-wider uppercase">Yönetim Paneli</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex items-center gap-2 text-xs">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                )}
                <div className="leading-tight">
                  <div className="font-medium">{user.name}</div>
                  <div className="text-muted-foreground">{user.role === "admin" ? "Admin" : "Yardımcı"}</div>
                </div>
              </div>
            )}
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-secondary/60 p-1 rounded-2xl flex flex-wrap h-auto gap-1 mb-8">
            {visibleTabs.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                data-testid={`admin-tab-${value}`}
                className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-display text-sm h-10 px-4"
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {visibleTabs.map(({ value, comp: Comp }) => (
            <TabsContent key={value} value={value} className="mt-0">
              <ErrorBoundary>
                <Comp />
              </ErrorBoundary>
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
